import React from 'react'
import {defineMessages} from 'react-intl'
import styled from '@emotion/styled'
import {FacilityIcon} from 'components/Accommodation/Facilities/FacilityIcon'
import {Hotel} from 'modules/sapiSearch/slice'
import {LovHit} from 'modules/search/slice'

import {Box} from '@vio/atlas/helpers/Box'
import {Icon, iconsList} from '@vio/atlas/Icon'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

const Wrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(110px, 1fr));
  grid-column-gap: ${cssTheme.layout.spacing.s600};
  grid-row-gap: ${cssTheme.layout.spacing.s250};
`

const properties: Record<string, keyof typeof iconsList> = {
  bathrooms: 'Bath',
  bedrooms: 'DoubleBed'
} as const

const messages = defineMessages({
  bathrooms: {
    id: 'bathroom',
    defaultMessage: '{value, plural, one {1 bathroom} other {# bathrooms}}'
  },
  bedrooms: {
    id: 'bedroom',
    defaultMessage: '{value, plural, one {1 bedroom} other {# bedrooms}}'
  }
})

// Being used to sort the `vrDetails` list based on the `reference` array
const reference = ['bedrooms', 'bathrooms']

const MAX_LIST_LENGTH = 6

interface Props {
  vrDetails?: Hotel['vrDetails']
  rankedFacilities?: LovHit[]
  className?: string
  showVrDetails: boolean
}

export const VacationRentalDetails = ({
  vrDetails = {},
  rankedFacilities = [],
  className = '',
  showVrDetails
}: Props) => {
  const vrDetailsArr = Object.keys(vrDetails)

  if (showVrDetails && vrDetailsArr.length === 0) return null

  const rankedFacilitiesMaxLength = showVrDetails
    ? Math.max(MAX_LIST_LENGTH - vrDetailsArr.length, 0)
    : MAX_LIST_LENGTH

  return (
    <div className={className} data-id="VacationRentalDetails">
      <Wrapper>
        {showVrDetails &&
          vrDetailsArr
            .sort((a, b) => reference.indexOf(a) - reference.indexOf(b))
            .map((key, index) => {
              if (vrDetails[key].count) {
                return (
                  <Box
                    display="flex"
                    width="100%"
                    alignItems="flex-start"
                    gap="s200"
                    key={index}
                  >
                    <Icon
                      size="sm"
                      name={properties[key]}
                      colorPath={'content.neutral.primary'}
                    />
                    <Text
                      variant={'bodyXS'}
                      colorPath={'content.neutral.primary'}
                    >
                      <FormattedMessageWrapper
                        {...messages[key]}
                        values={{value: vrDetails[key].count}}
                      />
                    </Text>
                  </Box>
                )
              }
            })}
        {rankedFacilities.slice(0, rankedFacilitiesMaxLength).map(facility => (
          <Box
            display="flex"
            width="100%"
            alignItems="flex-start"
            gap="s200"
            key={facility.id}
          >
            <FacilityIcon
              size="sm"
              facility={facility}
              colorPath={'content.neutral.primary'}
            />
            <Text variant={'bodyXS'} colorPath={'content.neutral.primary'}>
              {facility.value}
            </Text>
          </Box>
        ))}
      </Wrapper>
    </div>
  )
}
